<script setup lang="ts">
import { computed } from "vue"
import { Badge, FormField, FormLabel, FormDescription } from "@hantera/design-system"
import type { AppSettingDefinitionInfo } from "@/backend/apps.types"
import { resolveEditorComponent } from "./editors"

const props = defineProps<{
  modelValue: unknown
  definition: AppSettingDefinitionInfo
  settingKey: string
  readonly?: boolean
  hasStoredValue?: boolean
  markedForClearing?: boolean
}>()

const emit = defineEmits<{
  (e: "update:modelValue", value: unknown): void
  (e: "clear"): void
}>()

function resolveTranslation(
  dict: Record<string, string> | undefined,
  fallbackKey?: string
): string | undefined {
  if (!dict) return fallbackKey
  const userLang = document.documentElement.lang || "en"
  return dict[userLang] ?? dict["default"] ?? fallbackKey
}

const label = computed(() => resolveTranslation(props.definition.label, props.settingKey))
const description = computed(() => resolveTranslation(props.definition.description))

const editorComponent = computed(() => resolveEditorComponent(props.definition))

const showClearButton = computed(() => {
  return (
    props.definition.secret &&
    props.hasStoredValue &&
    !props.markedForClearing &&
    !props.readonly
  )
})
</script>

<template>
  <FormField>
    <div class="setting-label-row">
      <FormLabel>
        {{ label }}
        <Badge v-if="definition.secret" variant="neutral">Secret</Badge>
      </FormLabel>
      <button
        v-if="showClearButton"
        class="btn-silent btn-small"
        type="button"
        @click="emit('clear')"
      >
        Clear
      </button>
      <span v-if="markedForClearing" class="clear-pending">
        Will be cleared
      </span>
    </div>
    <template v-if="markedForClearing">
      <div class="setting-cleared-placeholder">Value will be cleared on save</div>
    </template>
    <component
      v-else
      :is="editorComponent"
      :model-value="modelValue"
      :definition="definition"
      :setting-key="settingKey"
      :readonly="readonly"
      :has-stored-value="hasStoredValue"
      @update:model-value="emit('update:modelValue', $event)"
    />
    <FormDescription v-if="description">
      {{ description }}
    </FormDescription>
  </FormField>
</template>

<style scoped>
.setting-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.setting-label-row .form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-silent {
  height: 20px;
  padding-left: 5px;
  padding-right: 5px;
}

.clear-pending {
  font-size: 0.75rem;
  font-style: italic;
  color: hsl(30 80% 45%);
}

.setting-cleared-placeholder {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-style: italic;
  color: hsl(var(--color-50));
  background-color: hsl(var(--color-95));
  border-radius: 0.25rem;
}
</style>
